/**
 * @module formatNumber
 */
import {
  ifNumberValidThen,
  getReadableInteger,
  getNumberParts,
  roundDecimal,
} from '../utils';
import { Precision, ParsableNumber } from '../typings';
import { CurrencyConfig } from '../config/currencyConfig';

/**
 * Format a number with given decimal places and seperator.
 * @param 	{string|number} input
 * @param		{?number|false|CurrencyConfig} options If pass number, it will be treated as precision.
 * @returns {string} Only valid number will be formated, otherwise it will return input it self.
 * @example
 * formatNumber(1.23456); // "1.23456"
 * formatNumber(1.23456, { precision: 3 }); // "1.235"
 * formatNumber(1.23456, { maxPrecision: 3 }); // "1.235"
 * formatNumber(1.2, { precision: 3 }); // "1.200"
 * formatNumber(1.2, { maxPrecision: 3 }); // "1.2"
 * formatNumber(1.2, { precision: 3, maxPrecision: 0 }); // "1"
 * formatNumber(1.2, { precision: 3, maxPrecision: 5 }); // "1.200"
 * formatNumber(123456.2); // "12,356.2"
 * formatNumber(1.23456, { precision: false }); // "1.2356"
 * formatNumber(123456, { precision: 1, decimalPoint: ',', separator: '.' }); // "12.356,0"
 * formatNumber(123456, { precision: 1, separator: (integerPart) => integerPart.replace(/\d{3}/, '$&-') }); // "123-56,0"
 */
export function formatNumber(
  input: ParsableNumber,
  options?: Precision | Partial<CurrencyConfig>
): ParsableNumber {
  const {
    precision: _precision,
    maxPrecision,
    decimalPoint = '.',
    separator,
    separatorPattern,
  } = (typeof options === 'object' && options) ||
  ({} as Partial<CurrencyConfig>);
  let precision = _precision;
  if (typeof options !== 'object') {
    precision = options;
  }

  const inputStr = String(input);
  const parsedInput = parseFloat(inputStr);
  // if it is not a valid number
  if (!isFinite(parsedInput)) {
    return '';
  }

  const roundPrecision = ifNumberValidThen(
    maxPrecision,
    (max: number) =>
      ifNumberValidThen(
        precision,
        (_precision) => Math.min(_precision, max),
        void 0
      ),
    precision
  );

  // if the number beyond the safe integer boundary
  // parseFloat would reduce the accuracy
  // we try to format it using string manipulation instead
  if (
    parsedInput > Number.MAX_SAFE_INTEGER ||
    parsedInput < Number.MIN_SAFE_INTEGER
  ) {
    const { symbol, integer, decimal } = getNumberParts(inputStr);
    const integerPartWithSep =
      typeof separator === 'function'
        ? separator(integer)
        : separator
        ? getReadableInteger(integer, { separator, separatorPattern })
        : integer;
    let roundedDecimal = '';
    if (decimal) {
      if (maxPrecision) roundedDecimal = decimal.slice(0, maxPrecision);
      if (roundPrecision) roundedDecimal = decimal.slice(0, roundPrecision);
    }
    return (
      symbol +
      integerPartWithSep +
      (roundedDecimal ? decimalPoint + roundedDecimal : '')
    );
  }

  // Parse the number into its components
  const { symbol, integer, decimal } = getNumberParts(inputStr);

  // Convert decimal part to a number (e.g., "99" -> 0.99, or 0 if no decimal)
  const decimalAsNumber = decimal ? parseFloat('0.' + decimal) : 0;

  // Round the decimal part: first to maxPrecision, then to roundPrecision
  const roundedDecimalStr = roundDecimal(
    roundDecimal(decimalAsNumber, maxPrecision),
    roundPrecision
  );

  // Parse the rounded result to check for carry-over
  const { integer: roundedInteger, decimal: roundedDecimal } =
    getNumberParts(roundedDecimalStr);

  // Handle carry-over from rounding (e.g., 0.99 -> 1.0, or 1.996 -> 2.0)
  const carryOver = Number(roundedInteger || '0');
  const finalInteger =
    carryOver >= 1
      ? String(Number(integer || '0') + carryOver)
      : integer || roundedInteger || '0';

  const finalDecimal = roundedDecimal || '';

  // Apply separator to the final integer part
  const integerPartWithSep =
    typeof separator === 'function'
      ? separator(finalInteger)
      : separator
      ? getReadableInteger(finalInteger, { separator, separatorPattern })
      : finalInteger;

  return (
    symbol +
    integerPartWithSep +
    (finalDecimal ? decimalPoint + finalDecimal : '')
  );
}
